(() => {
  const app = document.getElementById("quiz-app");
  const bar = document.querySelector(".quiz-progress-bar");
  if (!app || !bar) return;

  /* =========================
      STATE (WITH TRACK INTEGRATION)
     ========================= */
  const state = {
    step: "baseline",
    index: 0,
    goalIndex: 0,
    branchIndex: 0,
    answers: JSON.parse(localStorage.getItem("quiz_answers") || "{}"),
    history: []
  };

  const GOALS = [
    "Healthy aging & longevity",
    "Build muscle & recover better",
    "Heal injuries & reduce pain",
    "Improve metabolism & reduce belly fat",
    "Improve sleep & reset your body clock"
  ];

  /* =========================
      QUESTION BANK (STRICT MAP)
     ========================= */
  const Q = {
    baseline: [
      { id: "gender", type: "single", q: "What is your gender?", options: ["Male", "Female"] },
      { id: "age_years", type: "number", q: "How old are you?", min: 18, max: 100 },
      { id: "height_cm", type: "number", q: "What is your height?", min: 100, max: 250 },
      { id: "weight_kg", type: "number", q: "What is your current weight?", min: 30, max: 300 },
      { 
        id: "body_shape", 
        type: "single", 
        q: "Which body shape feels closest to yours today?", 
        options: [
          "Athletic — Visible muscle tone and a strong, active build.",
          "Lean — Naturally slimmer frame with minimal body fat or muscle.",
          "Normal — Balanced fat and muscle with no visible excess fat.",
          "Soft — Some fat around the waist or hips with less muscle definition.",
          "Abdominal — Most weight sits around the belly, even if arms or legs are slimmer.",
          "Generalized obesity — Body fat is distributed more broadly across the body."
        ] 
      }
    ],

    health: [
      { 
        id: "activity_level", 
        type: "single", 
        q: "How active are you in a typical week?", 
        options: [
          "Sedentary: Mostly sitting with little to no exercise",
          "Light activity: Occasional walking or movement, without a regular workout routine",
          "Moderate: Walking, cycling, or light workouts 2-3 times per week",
          "Active: Cardio or strength training 3-5 times per week",
          "Very active: Training almost daily or doing high-intensity workouts or sports"
        ]
      },

      { id: "has_medical_conditions", type: "single", q: "Do you have any ongoing medical conditions?", options: ["No", "Yes"] },

      {
        id: "medical_conditions",
        type: "multi",
        q: "Which conditions apply?",
        options: [
          "High blood pressure",
          "High cholesterol",
          "Prediabetes or diabetes",
          "Thyroid disorder",
          "Hormonal imbalance (for example low testosterone or PCOS)",
          "Autoimmune or inflammatory condition",
          "Chronic joint, muscle, or back pain",
          "Sleep disorder (insomnia, sleep apnea, etc.)",
          "Mental health condition (stress, anxiety, depression)",
          "Cardiovascular disease (heart attack, angina, heart failure)",
          "Liver disease",
          "Kidney disease",
          "History of cancer",
          "Other"
        ],
        showIf: (a) => a.has_medical_conditions === "Yes"
      },

      {
        id: "medical_conditions_other_text",
        type: "text",
        q: "Tell us which condition we should know about",
        showIf: (a) => (a.medical_conditions || []).includes("Other")
      },

      { id: "takes_regular_medications", type: "single", q: "Do you take any regular medications?", options: ["No", "Yes"] },

      {
        id: "regular_medications_text",
        type: "text",
        q: "List the medication name, dose, and how often you take it",
        showIf: (a) => a.takes_regular_medications === "Yes"
      },

      {
        id: "allergy_types",
        type: "multi",
        q: "Do you have any known allergies or sensitivities?",
        options: ["No known allergies", "Yes - medications", "Yes - foods", "Yes - other"]
      },

      {
        id: "allergy_detail",
        type: "text",
        q: "Tell us what you are allergic or sensitive to",
        showIf: (a) =>
          (a.allergy_types || []).some(v =>
            ["Yes - medications", "Yes - foods", "Yes - other"].includes(v)
          )
      },

      {
        id: "pregnancy_status",
        type: "single",
        q: "Are you currently pregnant?",
        options: ["No", "Yes"],
        showIf: (a) => a.gender === "Female"
      },

      {
        id: "breastfeeding_status",
        type: "single",
        q: "Are you currently breastfeeding?",
        options: ["No", "Yes"],
        showIf: (a) => a.gender === "Female"
      }
    ],

    branches: {
      "Healthy aging & longevity": [
        { id: "healthy_aging_energy_level", type: "single", q: "How would you rate your energy most days?", options: ["Very good", "Good", "Moderate", "Low"] },
        { id: "healthy_aging_recovery_decline", type: "single", q: "Have your recovery, resilience, or stamina declined recently?", options: ["No", "Slightly", "Moderately", "Significantly"] },
        { id: "healthy_aging_illness_frequency", type: "single", q: "How often do you get colds, flu, or infections?", options: ["Rarely", "1-2 times per year", "3-4 times per year", "Frequently"] },
        { id: "healthy_aging_inflammation", type: "single", q: "Do you deal with ongoing inflammation, aches, or stiffness?", options: ["No", "Occasionally", "Often", "Constantly"] },
        { id: "healthy_aging_optimization_focus", type: "single", q: "Is your focus long-term health optimization rather than treating a disease?", options: ["Yes", "Somewhat", "No"] }
      ],

      "Build muscle & recover better": [
        { id: "muscle_training_frequency", type: "single", q: "How often do you train or exercise?", options: ["0-1x per week", "2-3x per week", "4-5x per week", "6+ times per week"] },
        { id: "muscle_strength_plateau", type: "single", q: "Have your muscle gains or strength plateaued?", options: ["No", "Slightly", "Yes", "Declining"] },
        { id: "muscle_workout_recovery", type: "single", q: "How well do you recover after workouts?", options: ["Very well", "Acceptable", "Poorly", "Need several days"] },
        { id: "muscle_soreness_fatigue", type: "single", q: "How often do soreness or fatigue hold you back after training?", options: ["Rarely", "Sometimes", "Often", "Almost always"] },
        { id: "muscle_lean_mass_priority", type: "single", q: "How important is maintaining lean muscle for you?", options: ["Very important", "Somewhat important", "Not a priority"] }
      ],

      "Heal injuries & reduce pain": [
        { id: "injury_or_chronic_pain", type: "single", q: "Do you currently have an injury or chronic pain?", options: ["No", "Mild", "Moderate", "Severe"] },
        { id: "injury_issue_type", type: "single", q: "What best describes the issue?", options: ["Muscle strain", "Tendon or ligament", "Joint pain", "Post-surgery", "Multiple areas"] },
        { id: "injury_duration", type: "single", q: "How long has this been going on?", options: ["Less than 1 month", "1-3 months", "3-6 months", "More than 6 months"] },
        { id: "injury_activity_limitation", type: "single", q: "How much has it limited training or daily activity?", options: ["Not at all", "Slightly", "Moderately", "Completely"] },
        { id: "injury_treatment_response", type: "single", q: "Have conventional treatments helped?", options: ["Yes", "Partially", "No"] }
      ],

      "Improve metabolism & reduce belly fat": [
        { id: "metabolism_fat_storage", type: "single", q: "Where do you tend to store fat the most?", options: ["Evenly", "Hips or thighs", "Abdomen or belly", "Mostly visceral or belly fat"] },
        { id: "metabolism_fat_loss_resistance", type: "single", q: "Have you struggled to lose fat despite diet or exercise?", options: ["No", "Occasionally", "Yes", "Long-term struggle"] },
        { id: "metabolism_cravings_energy_crashes", type: "single", q: "How often do you get energy crashes or sugar cravings?", options: ["Rarely", "Sometimes", "Often", "Daily"] },
        { id: "metabolism_insulin_resistance_history", type: "single", q: "Have you been told you have insulin resistance, prediabetes, or metabolic syndrome?", options: ["No", "Borderline", "Yes"] },
        { id: "metabolism_appetite_challenge", type: "single", q: "Is appetite control a major challenge for you?", options: ["No", "Mild", "Moderate", "Severe"] }
      ],

      "Improve sleep & reset your body clock": [
        { id: "sleep_quality", type: "single", q: "How would you rate your sleep quality?", options: ["Very good", "Good", "Poor", "Very poor"] },
        { id: "sleep_issue_type", type: "single", q: "Do you struggle more with falling asleep or staying asleep?", options: ["Falling asleep", "Staying asleep", "Both", "Neither"] },
        { id: "sleep_hours_average", type: "single", q: "How many hours do you sleep on an average night?", options: ["Less than 5", "5-6", "6-7", "7-8", "More than 8"] },
        { id: "sleep_wake_refreshed", type: "single", q: "How often do you wake up feeling refreshed?", options: ["Always", "Sometimes", "Rarely", "Never"] },
        { id: "sleep_daytime_impact", type: "single", q: "How much does poor sleep affect your energy, mood, or recovery?", options: ["Not at all", "Slightly", "Moderately", "Severely"] }
      ]
    }
  };

  /* =========================
      UTILITIES
     ========================= */
  const save = () =>
    localStorage.setItem("quiz_answers", JSON.stringify(state.answers));

  const pushHistory = () => {
    state.history.push({
      step: state.step,
      index: state.index,
      goalIndex: state.goalIndex,
      branchIndex: state.branchIndex
    });
  };

  const goBack = () => {
    const last = state.history.pop();
    if (!last) return;

    state.step = last.step;
    state.index = last.index;
    state.goalIndex = last.goalIndex;
    state.branchIndex = last.branchIndex;

    if (state.step === "goals") {
      renderGoals();
    }
    else if (state.step === "contact") {
      renderContactDetails();
    }
    else {
      render(true);
    }
  };

  const isVisible = (q) => !q.showIf || q.showIf(state.answers);

  const list = (arr) => (arr || []).filter(isVisible);

  const current = () => {
    if (state.step === "baseline") return list(Q.baseline)[state.index];
    if (state.step === "health") return list(Q.health)[state.index];

    if (state.step === "branches") {
      const selected = state.answers.primary_goals || [];
      const g = selected[state.goalIndex];
      return list(Q.branches[g] || [])[state.branchIndex];
    }
    return null;
  };

  /* =========================
      RENDER METHODS
     ========================= */
  function render(isBack = false) {
    if (state.step === "contact") {
      renderContactDetails();
      return;
    }
    if (state.step === "goals") {
      renderGoals();
      return;
    }

    const q = current();
    if (!q) {
      if (state.step === "branches") {
        state.step = "contact";
        renderContactDetails();
      }
      return;
    }

    const progressLabel = getProgressLabel();

    const html = `
      <div class="quiz-card slide ${isBack ? "back" : "forward"}">
        <div class="topbar">
          <button class="back-btn" ${state.history.length ? "" : "disabled"}>←</button>
          <div class="step-label">${progressLabel}</div>
        </div>

        <h2>${q.q}</h2>

        <div class="input-container">
          ${renderInput(q)}
        </div>

        <button class="next">Continue</button>
      </div>
    `;

    app.innerHTML = html;
    updateProgress();

    const backBtn = app.querySelector(".back-btn");
    if (backBtn) backBtn.onclick = goBack;
  }

  function renderInput(q) {
    const saved = state.answers[q.id];

    if (q.type === "number") {
      return `<input type="number" min="${q.min}" max="${q.max}" value="${saved ?? ""}" placeholder="Enter number..." />`;
    }

    if (q.type === "text") {
      return `<input type="text" value="${saved ?? ""}" placeholder="Type your answer here..." />`;
    }

    return (q.options || []).map(o => {
      const selected = q.type === "multi"
        ? (saved || []).includes(o)
        : saved === o;

      return `
        <button class="opt ${selected ? "active" : ""}" data-val="${o}">
          ${o}
        </button>
      `;
    }).join("");
  }

  function renderGoals() {
    state.answers.primary_goals ||= [];
    const savedGoals = state.answers.primary_goals;

    app.innerHTML = `
      <div class="quiz-card">
        <div class="topbar">
          <button class="back-btn">←</button>
          <div class="step-label">Goals</div>
        </div>

        <h2>What are your main health goals?</h2>

        <div class="input-container">
          ${GOALS.map(g => `
            <button class="goal ${savedGoals.includes(g) ? "active" : ""}" data-g="${g}">
              ${g}
            </button>
          `).join("")}
        </div>

        <button id="goals-next">Continue</button>
      </div>
    `;

    updateProgress();
    app.querySelector(".back-btn").onclick = goBack;
  }

  function renderContactDetails() {
    const progressLabel = getProgressLabel();
    app.innerHTML = `
      <div class="quiz-card slide forward">
        <div class="topbar">
          <button class="back-btn">←</button>
          <div class="step-label">${progressLabel}</div>
        </div>

        <h2>Almost done! How can we reach you?</h2>

        <div class="input-container contact-form">
          <input
            type="text"
            id="full_name"
            placeholder="Full Name"
            value="${state.answers.full_name || ""}"
          />

          <input
            type="email"
            id="email"
            placeholder="Email Address"
            value="${state.answers.email || ""}"
          />

          <input
            type="tel"
            id="phone"
            placeholder="Contact Number"
            value="${state.answers.phone || ""}"
          />
        </div>

        <button class="contact-next">Continue</button>
      </div>
    `;

    updateProgress();
    app.querySelector(".back-btn").onclick = goBack;
  }

  function renderReview() {
    let bmiValue = "N/A";
    const w = state.answers.weight_kg;
    const h = state.answers.height_cm;

    if (w && h) {
      bmiValue = (w / Math.pow(h / 100, 2)).toFixed(2);
    }
    state.answers.bmi = bmiValue;

    const isFilled = (val) => {
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val) && val.length === 0) return false;
      return true;
    };

    const section = (title, items) => {
      const filledItems = items.filter(item => isFilled(item.value));
      if (filledItems.length === 0) return "";

      return `
        <div class="review-section">
          <h3>${title}</h3>
          <div class="review-grid">
            ${filledItems.map(item => `
              <div class="review-item">
                <strong>${item.label}</strong>
                <span>${item.value}</span>
              </div>
            `).join("")}
          </div>
        </div>
      `;
    };

    let html = `
      <div class="quiz-card review-screen">
        <h2>Review Your Profile</h2>
        <div class="bmi-badge">
          Calculated BMI: ${state.answers.bmi}
        </div>
    `;

    html += section("Baseline", [
      { label: "Gender", value: state.answers.gender },
      { label: "Age", value: state.answers.age_years },
      { label: "Height", value: state.answers.height_cm ? state.answers.height_cm + " cm" : "" },
      { label: "Weight", value: state.answers.weight_kg ? state.answers.weight_kg + " kg" : "" },
      { label: "Body Shape", value: state.answers.body_shape }
    ]);

    html += section("Health Background", [
      { label: "Activity Level", value: state.answers.activity_level },
      { label: "Medical Conditions", value: (state.answers.medical_conditions || []).join(", ") },
      { label: "Other Condition", value: state.answers.medical_conditions_other_text },
      { label: "Regular Medications", value: state.answers.regular_medications_text },
      { label: "Allergies", value: (state.answers.allergy_types || []).join(", ") },
      { label: "Allergy Details", value: state.answers.allergy_detail },
      { label: "Pregnant", value: state.answers.pregnancy_status },
      { label: "Breastfeeding", value: state.answers.breastfeeding_status }
    ]);

    if (state.answers.primary_goals && state.answers.primary_goals.length > 0) {
      html += `
        <div class="review-section">
          <h3>Selected Goals</h3>
          <div class="goal-tags">
            ${state.answers.primary_goals.map(goal => `
              <span class="goal-tag">${goal}</span>
            `).join("")}
          </div>
        </div>
      `;
    }

    html += section("Contact Details", [
      { label: "Full Name", value: state.answers.full_name },
      { label: "Email", value: state.answers.email },
      { label: "Phone", value: state.answers.phone }
    ]);

    html += `
        <button class="submit-btn">Submit Assessment</button>
      </div>
    `;

    app.innerHTML = html;
    bar.style.width = "100%";
  }

  /* =========================
      EVENT LISTENERS
     ========================= */
  document.addEventListener("click", (e) => {
    const opt = e.target.closest(".opt");
    if (opt) {
      const q = current();
      const val = opt.dataset.val;

      if (q.type === "multi") {
        state.answers[q.id] ||= [];
        const arr = state.answers[q.id];

        if (arr.includes(val)) {
          state.answers[q.id] = arr.filter(x => x !== val);
          opt.classList.remove("active");
        } else {
          state.answers[q.id] = [...arr, val];
          opt.classList.add("active");
        }
      } else {
        state.answers[q.id] = val;
        app.querySelectorAll(".opt").forEach(btn => {
          btn.classList.remove("active");
        });
        opt.classList.add("active");
      }

      save();
      return;
    }

    const next = e.target.closest(".next");
    if (next) {
      const q = current();
      const input = app.querySelector("input");

      if (input) {
        state.answers[q.id] = q.type === "number" ? Number(input.value) : input.value;
      }

      save();
      stepForward();
      return;
    }

    if (e.target.id === "goals-next") {
      const selected = state.answers.primary_goals || [];
      if (selected.length === 0) {
        alert("Please select at least one goal.");
        return;
      }
      pushHistory();
      state.step = "branches";
      state.goalIndex = 0;
      state.branchIndex = 0;
      render();
      return;
    }

    const contactNext = e.target.closest(".contact-next");
    if (contactNext) {
      const name = document.getElementById("full_name")?.value.trim();
      const email = document.getElementById("email")?.value.trim();
      const phone = document.getElementById("phone")?.value.trim();

      if (!name || !email || !phone) {
        alert("Please complete all fields.");
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert("Please enter a valid email address.");
        return;
      }

      state.answers.full_name = name;
      state.answers.email = email;
      state.answers.phone = phone;

      save();
      pushHistory();
      state.step = "review";
      renderReview();
      return;
    }

    const g = e.target.closest(".goal");
    if (g) {
      const val = g.dataset.g;
      state.answers.primary_goals ||= [];
      const arr = state.answers.primary_goals;

      state.answers.primary_goals = arr.includes(val)
        ? arr.filter(x => x !== val)
        : [...arr, val];

      save();
      renderGoals();
    }
  });

  /* =========================
      FLOW CONTROL ENGINE
     ========================= */
  function stepForward() {
    pushHistory();

    if (state.step === "baseline") {
      state.index++;
      if (state.index >= list(Q.baseline).length) {
        state.step = "health";
        state.index = 0;
      }
    } 
    else if (state.step === "health") {
      state.index++;
      if (state.index >= list(Q.health).length) {
        state.step = "goals";
      }
    } 
    else if (state.step === "branches") {
      const selected = state.answers.primary_goals || [];
      const currentGoalKey = selected[state.goalIndex];
      const branchQuestions = list(Q.branches[currentGoalKey] || []);

      state.branchIndex++;

      if (state.branchIndex >= branchQuestions.length) {
        state.goalIndex++;
        state.branchIndex = 0;

        if (state.goalIndex >= selected.length) {
          state.step = "contact";
        }
      }
    }
    else if (state.step === "contact") {
      state.step = "review";
    }

    if (state.step === "review") {
      renderReview();
    } else {
      render();
    }
  }

  function updateProgress() {
    const baselineLen = list(Q.baseline).length;
    const healthLen = list(Q.health).length;
    const goalsLen = 1; 
    const contactLen = 1;

    const selectedGoals = state.answers.primary_goals || [];
    let branchesLen = 0;
    selectedGoals.forEach(g => {
      branchesLen += list(Q.branches[g] || []).length;
    });

    const totalSteps = baselineLen + healthLen + goalsLen + branchesLen + contactLen;
    let completedSteps = 0;

    if (state.step === "baseline") {
      completedSteps = state.index;
    } 
    else if (state.step === "health") {
      completedSteps = baselineLen + state.index;
    } 
    else if (state.step === "goals") {
      completedSteps = baselineLen + healthLen;
    } 
    else if (state.step === "branches") {
      completedSteps = baselineLen + healthLen + goalsLen;
      for (let gIdx = 0; gIdx < state.goalIndex; gIdx++) {
        const goalKey = selectedGoals[gIdx];
        completedSteps += list(Q.branches[goalKey] || []).length;
      }
      completedSteps += state.branchIndex;
    }
    else if (state.step === "contact") {
      completedSteps = baselineLen + healthLen + goalsLen + branchesLen;
    }

    let percent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
    
    if (state.step === "review" || completedSteps > totalSteps) {
      percent = 100;
    }

    bar.style.width = `${Math.min(100, Math.max(0, percent))}%`;
  }

  function getProgressLabel() {
    if (state.step === "baseline") return "Your Baseline";
    if (state.step === "health") return "Health Background";
    if (state.step === "goals") return "Primary Health Objectives";
    if (state.step === "branches") return "Personalization";
    if (state.step === "contact") return "Final Step";
    return "Complete";
  }

  /* =========================
      INITIALIZATION
     ========================= */
  render();
})();